// Pure helpers behind "จำนวนของ SKU นี้มาจาก WMS" (shared stock, FE-1 —
// docs/specs/2026-10-01-shared-stock-design.md §8 + §11, mdf-bo-fn#301).
//
// WHAT CHANGED AND WHY IT MATTERS. Until now `mdfpf_product_sku.quantity` was
// 100% manual: staff typed it on /app-catalog/products/:id/edit (#72). With
// shared stock, a SKU whose `wms_map` resolves completely becomes a MIRROR of
// the warehouse — `services/stockshared` recomputes it every 20 s and
// overwrites whatever is stored. A staff member who keeps typing into that box
// is writing a number that is pushed over, silently, within the minute (§9 step
// 4 is explicit that this screen must land before `shared_mode: write`). So for
// those SKUs the input is replaced by the stored value + a badge, and the save
// payload DOES NOT CARRY `quantity` for them at all.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT (spec §8, BE-3):
//
//   GET /api/v1/bo/catalog/products/:id
//   200 {"data":{..., "skus":[{
//          "sku_code":"A", "quantity":89,
//          "stock_source":"wms",          // "wms" | "manual"
//          "wms_available":90,            // only when stock_source = "wms"
//          "pending_reserved":1           // only when stock_source = "wms"
//        }]}}
//
//   PUT /api/v1/bo/catalog/products/:id
//   200 {"data":{…},
//        "sku_notes":[{"sku_code":"A","stock_source":"wms",
//                      "quantity_ignored":true,"message":"…"}]}
//        `quantity_ignored` is the BE telling us it threw our number away. It
//        is sent for a client that is older than this change (or that ignores
//        the rule) — we strip `quantity` up front, so seeing it here means the
//        two sides disagree about a SKU and the operator is told.
//
//        THE ENVELOPE IS `sku_notes` AT THE TOP LEVEL, a SIBLING of `data` —
//        not a field inside `data.skus[]`. BE-3 deliberately keeps `data` byte
//        for byte what it was and adds the notes beside it, and only when the
//        save actually discarded something (controllers/Catalog_product.go,
//        catalogProductSaveResponse). This reader was written against the
//        `data.skus[]` shape and found nothing, so the toast never fired; both
//        shapes are accepted now and the spec table (§8) records the real one.
//
//   GET /api/v1/bo/catalog/stock/unmatched   (paginated, read-only, admin)
//   200 {"count":12,"unmapped":9,"unmatched":3,"page":1,"limit":100,
//        "scan_truncated":false,
//        "warnings":[{"code":"wms_unmapped"|"wms_unmatched",
//                     "sku_code":"SHINE50+MIXC1","sku_id":441,
//                     "bo_product_id":77,"stock_source":"manual",
//                     "expected":2,"mapped":1,"wms_unmatched":[…],
//                     "message":"…"}]}
//        Same two warning codes as the Shopee seller mapper and
//        services/stockshared (WarnUnmapped / WarnUnmatched, stockshared.go:38).
//        THE PRODUCT ID IS `bo_product_id` — the mdfpf_product_sku column name,
//        not `product_id`. Reading only `product_id` (as this module first did)
//        leaves every row without a link to the editor.
//
// `stock_source` IS THE ONLY GATE — deliberately NOT `wms_map`. Four things
// besides an empty map make a SKU manual (spec §5 step 4 / Q3 / Q6): an entry
// with wms_product_id = 0, pickup_quantity <= 0, a wms_product that no longer
// exists, and `wms_product.stock_sync_enabled = 0`. A FE that locked the input
// on "wms_map is non-empty" would lock staff out of the number on exactly the
// SKUs that still need a manual one — the half-mapped ones (#583).
//
// AN API THAT DOES NOT SEND `stock_source` AT ALL behaves as it did before this
// change: editable input, nothing stripped, no badge. That is not politeness,
// it is the rollback path — `stock.shared_enabled: false` (§9 step 6) means
// every SKU is manual again, and a FE that read a missing field as "wms" would
// freeze the whole catalogue's quantities with no way back.
// --------------------------------------------------------------------------
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see reference-bo-fn-jest-infra + tests/unit/skuWmsOptionsSource.spec.js), so
// every rule worth testing lives here. Behaviour: tests/unit/stockSource.spec.js.

import { wmsUnmatchedCount } from './wmsCoverage';

/** The two values `stock_source` can take. */
export const STOCK_SOURCE_WMS = 'wms';
export const STOCK_SOURCE_MANUAL = 'manual';

/** The two warning codes /stock/unmatched can report (stockshared.go:37-39). */
export const WARN_UNMAPPED = 'wms_unmapped';
export const WARN_UNMATCHED = 'wms_unmatched';

/** Coerce anything the API might send into a non-negative integer. */
function toCount(v) {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
}

/** A finite integer >= 0, or null when the field is absent / not a number. */
function toIntOrNull(v) {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
}

function text(v) {
  if (v === null || v === undefined || typeof v === 'object') return '';
  return String(v).trim();
}

/** The first argument that is neither undefined nor null. */
function firstDefined(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

/**
 * stockSourceOf(sku) — the normalised `stock_source`, or '' when the API did
 * not send one (older BE, or the field was dropped).
 *
 * Lower-cased and trimmed: the value is a wire enum, and a build that showed
 * the input for "WMS" while the BE pushed over it would be the exact bug this
 * module exists to prevent.
 */
export function stockSourceOf(sku) {
  if (!sku || typeof sku !== 'object') return '';
  const raw = sku.stock_source !== undefined ? sku.stock_source : sku.stockSource;
  return text(raw).toLowerCase();
}

/** isWmsSourced(sku) — is this SKU's quantity owned by the warehouse? */
export function isWmsSourced(sku) {
  return stockSourceOf(sku) === STOCK_SOURCE_WMS;
}

/**
 * isManualSourced(sku) — did the API EXPLICITLY say "manual"?
 *
 * false for a SKU with no `stock_source`, which is why this is not
 * `!isWmsSourced`: the manual hint ("ยังไม่ได้แมปสินค้าคลัง") is a statement
 * about shared stock, and printing it on every SKU of a build talking to an
 * older API would be noise on rows the feature does not know anything about.
 */
export function isManualSourced(sku) {
  return stockSourceOf(sku) === STOCK_SOURCE_MANUAL;
}

/** wmsAvailableOf(sku) — `wms_available`, or null when not sent. */
export function wmsAvailableOf(sku) {
  if (!sku || typeof sku !== 'object') return null;
  return toIntOrNull(sku.wms_available !== undefined ? sku.wms_available : sku.wmsAvailable);
}

/** pendingReservedOf(sku) — `pending_reserved`, or null when not sent. */
export function pendingReservedOf(sku) {
  if (!sku || typeof sku !== 'object') return null;
  return toIntOrNull(sku.pending_reserved !== undefined ? sku.pending_reserved : sku.pendingReserved);
}

/**
 * wmsHint(sku, t) — the sentence under a locked quantity.
 *
 * `t` is vue-i18n's $t(key, params), passed in so this module stays Vue-free
 * and the wording stays in src/languages/*.
 *
 *   both numbers present -> app_catalog.sku_quantity_from_wms_hint_detail
 *                           "…(คงเหลือ {available} − รอตัด {pending})…"
 *   otherwise            -> app_catalog.sku_quantity_from_wms_hint  (no numbers)
 *
 * THE FALLBACK IS LOAD-BEARING. `available = wms_available − pending_reserved`
 * is the identity the operator is being shown; printing "รอตัด 0" because the
 * field was absent would assert something about in-flight orders that nobody
 * measured. A BE that sends `stock_source` but not the diagnostics (the
 * HasColumn guards of §3.1 can legitimately leave them out) gets the plain
 * sentence, which is still true.
 *
 * '' for a SKU that is not WMS-sourced — there is nothing to explain.
 */
export function wmsHint(sku, t) {
  const translate = typeof t === 'function' ? t : (key) => key;
  if (!isWmsSourced(sku)) return '';
  const available = wmsAvailableOf(sku);
  const pending = pendingReservedOf(sku);
  if (available === null || pending === null) {
    return translate('app_catalog.sku_quantity_from_wms_hint');
  }
  return translate('app_catalog.sku_quantity_from_wms_hint_detail', { available, pending });
}

/**
 * stripWmsQuantities(payloadSkus, skus) — remove `quantity` from the save
 * payload of every WMS-sourced SKU. Returns a NEW array; neither argument is
 * mutated (the form rows are reactive Vue state and the payload is built from
 * them one line earlier).
 *
 * WHY STRIP RATHER THAN SEND THE STORED VALUE. Sending it back looks harmless
 * and is not: `quantity` is a mirror refreshed every 20 s, so the number this
 * page loaded is already stale by the time save is pressed, and writing it
 * would push the shop BACKWARDS to a stock level that may have sold out. The
 * BE ignores it too (§8, "ป้องกัน client เก่า") — this is the near side of the
 * same rule, and `quantity_ignored` in the response is how the two sides
 * reconcile when they disagree.
 *
 * ALIGNMENT: positional when the two arrays are the same length — which is the
 * real case, since the payload is `form.skus.map(...)`. When they are not
 * (a caller that filtered, or a test), fall back to matching on `sku_code`.
 * Positional always wins where it applies: two SKUs can carry the same
 * placeholder code before the WMS modal has run, and a code lookup would then
 * strip the wrong row.
 */
export function stripWmsQuantities(payloadSkus, skus) {
  if (!Array.isArray(payloadSkus)) return [];
  const rows = Array.isArray(skus) ? skus : [];
  const aligned = rows.length === payloadSkus.length;

  const wmsCodes = new Set();
  rows.forEach((row) => {
    if (!isWmsSourced(row)) return;
    const code = text(row.sku_code);
    if (code !== '') wmsCodes.add(code);
  });

  return payloadSkus.map((entry, i) => {
    if (!entry || typeof entry !== 'object') return entry;
    const locked = aligned
      ? isWmsSourced(rows[i])
      : wmsCodes.has(text(entry.sku_code));
    if (!locked) return entry;
    const copy = { ...entry };
    delete copy.quantity;
    return copy;
  });
}

/**
 * quantityIgnoredSkus(response) — the sku_codes the BE says it ignored our
 * `quantity` for, read out of a PUT/POST response.
 *
 * Accepts the axios response, its `.data`, the `{data:{…}}` envelope, a bare
 * product object, or a bare array of SKUs: the save path differs between
 * create and update and this must not be the reason a toast is missed.
 *
 * A SKU with no code still counts (the operator is told the number), it simply
 * contributes '' to no name — callers use the LENGTH for the toast and the
 * codes only as detail.
 */
export function quantityIgnoredSkus(response) {
  const entries = skuNotes(response).concat(responseSkus(response));
  const out = [];
  const seen = new Set();
  entries.forEach((sku) => {
    if (!sku || typeof sku !== 'object') return;
    const flag = sku.quantity_ignored !== undefined ? sku.quantity_ignored : sku.quantityIgnored;
    if (flag !== true && flag !== 1 && flag !== '1' && flag !== 'true') return;
    const code = text(sku.sku_code !== undefined ? sku.sku_code : sku.skuCode);
    // A code may be reported in `sku_notes` AND (by a future BE) inside
    // `data.skus[]`; the operator is told once per SKU, not twice. '' is kept
    // once, because a note with no code still means "your number did not land".
    const key = code === '' ? '\u0000empty' : code;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(code);
  });
  return out;
}

/**
 * skuNotes(response) — the top-level `sku_notes` array BE-3 actually sends,
 * reached through however many `data` wrappers the caller handed us (axios
 * response, its body, or the body already unwrapped).
 *
 * Stops at the FIRST level that carries `sku_notes`: unwrapping past it would
 * walk into `data` (the product) and lose the array.
 */
function skuNotes(response) {
  let node = response;
  for (let hops = 0; node && typeof node === 'object' && !Array.isArray(node) && hops < 3; hops += 1) {
    if (Array.isArray(node.sku_notes)) return node.sku_notes;
    if (Array.isArray(node.skuNotes)) return node.skuNotes;
    if (node.data === undefined) break;
    node = node.data;
  }
  return [];
}

function responseSkus(response) {
  let node = response;
  // axios response -> body
  if (node && typeof node === 'object' && node.data !== undefined && !Array.isArray(node)) {
    // {data:{data:{…}}} (axios + API envelope) and {data:{…}} both land here;
    // unwrap while the next level is still an object carrying `data`.
    let hops = 0;
    while (node && typeof node === 'object' && !Array.isArray(node) && node.data !== undefined && hops < 3) {
      if (Array.isArray(node.skus)) break; // this level already is the product
      node = node.data;
      hops += 1;
    }
  }
  if (Array.isArray(node)) return node;
  if (!node || typeof node !== 'object') return [];
  if (Array.isArray(node.skus)) return node.skus;
  if (Array.isArray(node.Skus)) return node.Skus;
  return [];
}

/**
 * unmatchedBannerCount(list) — the number in the banner on
 * /app-catalog/products ("มี SKU ที่ WMS ไม่ครบ {n} รายการในหน้านี้").
 *
 * Two shapes on purpose:
 *   an ARRAY  = the loaded product rows -> sum of each row's
 *               `wms_unmatched_count` (the #302 field, already LIVE), which is
 *               the number of MISSING warehouse codes, not of SKUs;
 *   an OBJECT = a /stock/unmatched body -> its own `count` / `total`.
 *
 * The array form is PAGE-LOCAL and the banner's wording says so. The list
 * endpoint has no tenant-wide counter, and a banner that implied one would have
 * an operator read "0" on page 1 as "the catalogue is clean" — the same trap the
 * #302 filter hint exists to avoid. The authoritative number is on the page the
 * banner links to.
 */
export function unmatchedBannerCount(list) {
  if (Array.isArray(list)) {
    return list.reduce((sum, row) => sum + wmsUnmatchedCount(row), 0);
  }
  if (list && typeof list === 'object') {
    const direct = toCount(list.count);
    return direct > 0 ? direct : toCount(list.total);
  }
  return 0;
}

/**
 * normaliseUnmatchedRows(body) — the /stock/unmatched payload as table rows:
 * { code, skuCode, productId, productName, message, parts, missing }.
 *
 * Tolerant about the envelope (`warnings` per §8, or `items` / `data` if BE-3
 * lands paginated under a different name) and about the per-row spelling, since
 * the handler does not exist yet at the time this is written — but NOT tolerant
 * about `sku_code`: a row that does not say WHICH SKU is short is not
 * actionable, so it is dropped rather than rendered as a blank line.
 */
export function normaliseUnmatchedRows(body) {
  const list = unmatchedList(body);
  const out = [];
  list.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const skuCode = text(entry.sku_code !== undefined ? entry.sku_code : entry.skuCode);
    if (skuCode === '') return;
    const parts = unmatchedParts(entry);
    const expected = toCount(entry.expected);
    const mapped = toCount(entry.mapped);
    out.push({
      code: text(entry.code || entry.warning || entry.warning_code).toLowerCase(),
      skuCode,
      // `bo_product_id` FIRST, because that is the name BE-3 sends — it is the
      // column name on mdfpf_product_sku (Catalog_stock_unmatched.go,
      // boStockUnmatchedWarning.BoProductID). Reading only `product_id` left
      // every row of the real response with productId 0, which renders the
      // product cell as "-" and hides the edit link on the whole page.
      productId: toCount(firstDefined(
        entry.bo_product_id, entry.boProductId, entry.product_id, entry.productId
      )),
      productName: text(
        entry.product_name || entry.product_name_th || entry.name_th || entry.name
      ),
      message: text(entry.message || entry.msg),
      parts,
      missing: Math.max(expected - mapped, parts.length),
    });
  });
  return out;
}

function unmatchedList(body) {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return [];
  const candidates = [body.warnings, body.Warnings, body.items, body.data, body.Datum];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
    // one more hop for the axios/{data:{…}} envelope
    if (c && typeof c === 'object') {
      const nested = [c.warnings, c.Warnings, c.items, c.data];
      for (const n of nested) {
        if (Array.isArray(n)) return n;
      }
    }
  }
  return [];
}

// The named parts of a shortfall. `wms_unmatched` is the #302 spelling (strings
// — warehouse codes); `unmatched` is what services/stockshared carries
// (wms_product IDS, which are numbers). Both are rendered as text, and numbers
// are prefixed with '#' so an id is never mistaken for a man_pn.
function unmatchedParts(entry) {
  const raw = Array.isArray(entry.wms_unmatched)
    ? entry.wms_unmatched
    : (Array.isArray(entry.unmatched) ? entry.unmatched : (Array.isArray(entry.missing_parts) ? entry.missing_parts : []));
  const out = [];
  raw.forEach((p) => {
    if (typeof p === 'number') {
      if (isFinite(p) && p > 0) out.push('#' + String(Math.floor(p)));
      return;
    }
    const s = text(p);
    if (s !== '') out.push(s);
  });
  return out;
}

/**
 * unmatchedRowI18nKey(row) — the i18n key for a row's warning code.
 *
 * The two codes mean different work: `wms_unmapped` is "nobody has mapped this
 * SKU yet" (open the WMS modal), `wms_unmatched` is "it IS mapped, at something
 * that is not there" (a deleted warehouse item, or stock_sync_enabled = 0 —
 * which is NOT fixed in the same place). An unknown/absent code falls back to
 * the generic label rather than to one of the two, because guessing sends the
 * operator to the wrong screen.
 */
export function unmatchedRowI18nKey(row) {
  const code = row && typeof row === 'object' ? text(row.code).toLowerCase() : '';
  if (code === WARN_UNMAPPED) return 'app_catalog.stock_unmatched_code_unmapped';
  if (code === WARN_UNMATCHED) return 'app_catalog.stock_unmatched_code_unmatched';
  return 'app_catalog.stock_unmatched_code_unknown';
}

/**
 * unmatchedEditPath(row) — the product editor link, or '' when the row carries
 * no product id.
 *
 * '' IS THE CASE THAT MATTERS: §8's response shape is per sku_code and need not
 * carry a product id at all. An `<a href="">` on such a row reloads the page the
 * operator is reading, so the caller renders plain text whenever this is ''.
 */
export function unmatchedEditPath(row) {
  const id = row && typeof row === 'object'
    ? toCount(firstDefined(row.productId, row.bo_product_id, row.boProductId, row.product_id))
    : 0;
  if (id <= 0) return '';
  return '/seller/products/' + id + '/edit';
}

export default {
  STOCK_SOURCE_WMS,
  STOCK_SOURCE_MANUAL,
  WARN_UNMAPPED,
  WARN_UNMATCHED,
  stockSourceOf,
  isWmsSourced,
  isManualSourced,
  wmsAvailableOf,
  pendingReservedOf,
  wmsHint,
  stripWmsQuantities,
  quantityIgnoredSkus,
  unmatchedBannerCount,
  normaliseUnmatchedRows,
  unmatchedRowI18nKey,
  unmatchedEditPath,
};
